import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import {
  useTelegramLink, useTelegramPrefs, useTelegramStatus, useTelegramUnlink,
} from './api.ts';
import { zoneErrorMessage } from './errors.ts';

const POLL_MS = 3000;
const POLL_MAX_MS = 2 * 60 * 1000;

/** Telegram link/unlink + notification switch for zone users. Hidden when the server has Telegram off. */
export function TelegramCard(): JSX.Element | null {
  const status = useTelegramStatus();
  const link = useTelegramLink();
  const unlink = useTelegramUnlink();
  const prefs = useTelegramPrefs();
  const [waiting, setWaiting] = useState(false);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const deadline = useRef(0);

  const linked = status.data?.linked ?? false;
  const { refetch } = status;

  // After opening Telegram, poll every 3s for up to 2 min or until linked; cleared on unmount.
  useEffect(() => {
    if (!waiting) return;
    if (linked) { setWaiting(false); return; }
    const t = setInterval(() => {
      if (Date.now() > deadline.current) { setWaiting(false); return; }
      void refetch();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [waiting, linked, refetch]);

  if (status.isLoading || !status.data || !status.data.enabled) return null;

  const startLink = (): void => {
    setError(null);
    link.mutate(undefined, {
      onSuccess: ({ url }) => {
        // iOS Safari may block this async popup; the visible anchor below is the fallback.
        setLinkUrl(url);
        window.open(url, '_blank', 'noopener');
        deadline.current = Date.now() + POLL_MAX_MS;
        setWaiting(true);
      },
      onError: (e) => setError(zoneErrorMessage(e)),
    });
  };

  const onFail = (e: unknown): void => setError(zoneErrorMessage(e));
  const notify = status.data.telegramNotifications;

  return (
    <section className="card-paper space-y-4 p-5">
      <h2 className="font-serif text-xl text-ink leading-tight">Telegram</h2>
      {linked ? (
        <>
          <p className="flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--success))]">
            <Icon name="check_circle" size={18} /> Linked
          </p>
          <div className="flex items-center justify-between gap-4">
            <label htmlFor="tg-notify" className="text-sm text-foreground">Send me issue updates on Telegram</label>
            <button
              id="tg-notify"
              type="button"
              role="switch"
              aria-checked={notify}
              disabled={prefs.isPending}
              onClick={() => { setError(null); prefs.mutate(!notify, { onError: onFail }); }}
              className={`relative h-8 w-14 shrink-0 rounded-full border transition-colors disabled:opacity-60 ${
                notify ? 'border-primary bg-primary' : 'border-[hsl(var(--border-strong))] bg-transparent'
              }`}
            >
              <span
                className={`absolute top-1/2 size-5 -translate-y-1/2 rounded-full transition-all ${
                  notify ? 'start-[1.9rem] bg-primary-foreground' : 'start-1.5 bg-muted-foreground'
                }`}
              />
            </button>
          </div>
          <button
            type="button"
            className="btn-ghost"
            disabled={unlink.isPending}
            onClick={() => { setError(null); unlink.mutate(undefined, { onError: onFail }); }}
          >
            Unlink
          </button>
        </>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">Get issue updates on Telegram</p>
          <button type="button" className="btn-primary" disabled={link.isPending} onClick={startLink}>
            <Icon name="send" size={18} /> Link Telegram
          </button>
          {waiting && linkUrl && (
            <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary">
              <Icon name="open_in_new" size={18} /> Open Telegram
            </a>
          )}
          {waiting && (
            <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
              Waiting for you to press Start in Telegram…
            </p>
          )}
        </>
      )}
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
    </section>
  );
}
